import { CanonicalGameSimulation, type CanonicalGameFrame } from "./canonicalGameSimulation";
import { applyDesktopArtboardLayout } from "./desktopArtboard";

export const GAME_SEQUENCE_HEADINGS = [
  "Создавай 3D-игры",
  "Создавай игровые 3D объекты и анимации в Blender",
  "Переноси разработанные объекты в игровую среду",
] as const;

export const GAME_SEQUENCE_TIMINGS = {
  buildAt: 3_600,
  transferAt: 5_000,
  simulationAt: 6_500,
  uiAt: 8_500,
  uiDuration: 1_100,
  headingEnter: 950,
  headingExit: 420,
} as const;

export type GameSequencePhase = "title" | "build" | "transfer" | "playing" | "exit";
export interface GameSequenceFrame {
  readonly elapsedMs: number;
  readonly phase: GameSequencePhase;
  readonly headingIndex: 0 | 1 | 2;
  readonly headingText: typeof GAME_SEQUENCE_HEADINGS[number];
  readonly headingPresence: number;
  readonly tunnelPresence: number;
  readonly uiPresence: number;
  readonly readProgress: number;
  readonly thesisPresence: readonly [number, number];
  readonly canonical: CanonicalGameFrame | null;
}

const clamp = (value: number): number => Math.max(0, Math.min(1, value));
const smooth = (value: number): number => { const t = clamp(value); return t * t * (3 - 2 * t); };
const T = GAME_SEQUENCE_TIMINGS;

/** One persistent title, followed by two theses and the actual product.
 * The miniature consumes the same score and explicitly requests departure. */
export function sampleGameSequence(elapsedMs: number, canonical: CanonicalGameFrame | null = null): GameSequenceFrame {
  const time = Math.max(0, elapsedMs);
  const headingIndex = time < T.buildAt ? 0 : time < T.transferAt ? 1 : 2;
  // The source physics clamps stalled frames. Its own clock must also pace
  // UI reveal, otherwise a slow device can reveal controls before landing.
  const uiPresence = canonical && canonical.entry >= 1
    ? smooth((canonical.elapsed * 1_000 - (T.uiAt - T.simulationAt)) / T.uiDuration)
    : 0;
  return {
    elapsedMs: time,
    phase: canonical && canonical.exit > 0 ? "exit"
      : time < T.buildAt ? "title"
        : time < T.transferAt ? "build"
          : uiPresence < 1 ? "transfer" : "playing",
    headingIndex,
    headingText: GAME_SEQUENCE_HEADINGS[0],
    headingPresence: smooth(time / T.headingEnter),
    tunnelPresence: smooth((time - T.buildAt) / 1_100),
    uiPresence,
    readProgress: smooth((time - 850) / 2_350),
    thesisPresence: [smooth((time-T.buildAt)/900),smooth((time-T.transferAt)/900)],
    canonical,
  };
}

export class GameSequenceDirector {
  private readonly simulation = new CanonicalGameSimulation(undefined,'showcase');
  private elapsedMs = 0;
  private lastNow: number | null = null;
  private simulationStarted = false;
  private lastCanonical: CanonicalGameFrame | null = null;

  reset(now: number, startOffsetMs = 0): void {
    this.elapsedMs = Math.max(0, startOffsetMs);
    this.lastNow = now;
    this.simulationStarted = false;
    this.lastCanonical = null;
  }

  advance(now: number, exitIntent = 0, reverseExit = false): GameSequenceFrame {
    if (this.lastNow === null) this.reset(now);
    this.elapsedMs += Math.max(0, Math.min(100, now - this.lastNow!));
    this.lastNow = now;
    let canonical: CanonicalGameFrame | null = null;
    if (this.elapsedMs >= T.simulationAt) {
      if (!this.simulationStarted) {
        this.simulation.reset(now);
        this.simulationStarted = true;
      }
      // Complete the actual entrance before a fast scroll may request exit.
      const readyForExit = this.lastCanonical !== null
        && this.lastCanonical.elapsed * 1_000 >= T.uiAt + T.uiDuration - T.simulationAt;
      canonical = this.simulation.advance(now, readyForExit ? exitIntent : 0, reverseExit);
      this.lastCanonical = canonical;
    }
    return sampleGameSequence(this.elapsedMs, canonical);
  }

  /**
   * Ход раздела от прокрутки, а не от настенных часов.
   *
   * Физика остаётся ровно прежней: шаг фиксированный, просто за один кадр их
   * делается столько, сколько человек пролистал. Одним большим шагом догонять
   * нельзя — у интегратора от этого меняется и высота прыжка, и время
   * переворота, о чём прямо написано в `CanonicalGameSimulation.advance`.
   *
   * Назад пробег не отматывается: симуляция необратима. Человек, вернувшийся
   * вверх внутри раздела, видит уже сыгранное состояние — ровно так же, как на
   * компьютере ведёт себя уже просмотренная глава.
   */
  advanceTo(targetMs: number, exitIntent = 0, reverseExit = false): GameSequenceFrame {
    const step = 1_000 / 60;
    /* Потолок догона за кадр. При 24 кадрах в секунду это больше пятнадцати
       секунд симуляции на секунду времени — быстрее любого маха пальцем. */
    const budget = 40;
    if (this.lastNow === null) this.reset(0);
    let taken = 0;
    let frame: GameSequenceFrame | null = null;
    while (this.elapsedMs + 1 < targetMs && taken < budget) {
      frame = this.advance(this.lastNow! + step, exitIntent, reverseExit);
      taken += 1;
    }
    /* Даже стоя на месте раздел обязан получить кадр: у выхода из туннеля своя
       доля, и она приходит из прокрутки, а не из накопленного времени. */
    return frame ?? this.advance(this.lastNow!, exitIntent, reverseExit);
  }
}

/** Apply to #uses-game or the highlight's .marketing-inner copy. No clone,
 * renderer or independent timer is created here. The director owns motion. */
export function applyGameSequencePresentation(host: HTMLElement, state: GameSequenceFrame): void {
  host.dataset.gameSequence = state.phase;
  host.style.setProperty("--game-sequence-tunnel", String(state.tunnelPresence));
  host.style.setProperty("--game-sequence-ui", String(state.uiPresence));
  host.style.setProperty("--game-sequence-heading", String(state.headingPresence));
  host.style.setProperty("--game-sequence-heading-softness", `${(1 - state.headingPresence) * 3}px`);
  host.style.setProperty("--game-sequence-heading-scale", String(1 + (1 - state.headingPresence) * 0.012));
  const heading = host.querySelector<HTMLElement>(".marketing-copy h2");
  if (heading) {
    if(heading.dataset.gameSequenceHeading!=='title')heading.textContent=GAME_SEQUENCE_HEADINGS[0];
    heading.dataset.gameSequenceHeading = 'title';
  }
  host.style.setProperty('--game-thesis-first',String(state.thesisPresence[0]));
  host.style.setProperty('--game-thesis-second',String(state.thesisPresence[1]));
  // Entering the director changes title wrapping and therefore the available
  // frame height without a window resize. Refresh the source fit in both hosts.
  const frame = host.querySelector<HTMLElement>(".uses-desktop-frame");
  if (frame) applyDesktopArtboardLayout(frame);
}
